import React, { useState, useEffect } from 'react';
import Logo from '../../components/common/Logo';
import Sidebar from '../../components/patient/PatientSidebar';
import { Save, Calendar, Mail, Phone, FileText, MapPin } from 'lucide-react';
import PatientService from '../../services/patient.service';
import PatientChangePasswordBox from '../../components/patient/PatientChangePasswordBox';
import { formatDate } from '../../utils/dateUtils';
import moment from 'moment';

const PatientProfilePage = () => {
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    birth_date: '',
    birth_place: '',
    pi: '',
    cf: '',
    gender: '',
    address: '',        // Campo generico (mantenuto per compatibilità)
    street: '',         // Via
    civic_number: '',   // Numero civico
    postal_code: '',    // CAP
    city: '',           // Città
    province: '',       // Provincia
    notes: '',
    medical_history: '',
    created_at: '',
    invio_fattura_sistema_sanitaria: true,
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const data = await PatientService.getProfile();

      const getBirthDate = data.user.birth_date
      const birthDate = moment(getBirthDate, 'DD/MM/YYYY').format('YYYY-MM-DD');

      setProfileData({
        first_name: data.user.first_name || '',
        last_name: data.user.last_name || '',
        email: data.user.email || '',
        phone: data.user.phone || '',
        birth_date: birthDate || '',
        birth_place: data.user.birth_place || '',
        pi: data.pi || '',
        cf: data.cf || '',
        gender: data.user.gender || '',
        address: data.user.address || '',
        street: data.user.street || '',
        civic_number: data.user.civic_number || '',
        postal_code: data.user.postal_code || '',
        city: data.user.city || '',
        province: data.user.province || '',
        notes: data.user.notes || '',
        medical_history: data.medical_history || '',
        created_at: data.user.created_at,
        invio_fattura_sistema_sanitaria: data.invio_fattura_sistema_sanitaria ?? true
      });

    } catch (error) {
      console.error('Error loading profile:', error);
      setError('Errore nel caricamento del profilo');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCheckboxChange = (field, checked) => {
    setProfileData(prev => ({
      ...prev,
      [field]: checked
    }));
  };

  const handleSave = async () => {
    // Validazione frontend di TUTTI i campi indirizzo + CF
    const errors = [];

    if (!profileData.birth_date || profileData.birth_date.trim() === '') {
      errors.push('Data di Nascita è obbligatorio');
    }

    if (!profileData.birth_place || profileData.birth_place.trim() === '') {
      errors.push('Luogo di Nascita è obbligatorio');
    }

    // CF obbligatorio solo frontend
    if (!profileData.cf || profileData.cf.trim() === '') {
      errors.push('Codice Fiscale è obbligatorio');
    }

    // Campi indirizzo obbligatori
    if (!profileData.street || profileData.street.trim() === '') {
      errors.push('Via/Strada è obbligatorio');
    }

    if (!profileData.civic_number || profileData.civic_number.trim() === '') {
      errors.push('Numero civico è obbligatorio');
    }

    if (!profileData.postal_code || profileData.postal_code.trim() === '') {
      errors.push('CAP è obbligatorio');
    }

    if (!profileData.city || profileData.city.trim() === '') {
      errors.push('Città è obbligatorio');
    }

    if (!profileData.province || profileData.province.trim() === '') {
      errors.push('Provincia è obbligatorio');
    }

    if (errors.length > 0) {
      setError('Campi obbligatori: ' + errors.join(', '));
      setSuccess('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (window.confirm('Sei sicuro di voler salvare le modifiche?')) {
      try {
        await PatientService.updateProfile(profileData);
        setSuccess('Profilo aggiornato con successo');
        setError('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (error) {
        setError('Errore durante l\'aggiornamento del profilo');
        setSuccess('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  // Helper per mostrare l'indirizzo completo nella sidebar
  const getFullAddress = () => {
    const parts = [];
    if (profileData.street) {
      parts.push(profileData.street + (profileData.civic_number ? ' ' + profileData.civic_number : ''));
    }
    if (profileData.postal_code && profileData.city) {
      parts.push(profileData.postal_code + ' ' + profileData.city);
    }
    if (profileData.province) {
      parts.push('(' + profileData.province + ')');
    }
    return parts.length > 0 ? parts.join(', ') : 'Non specificato';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-[#FAF4EB]">
        <div className="text-xl text-gray-600">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <div className="w-64 bg-white shadow-md">
        <div className="p-4">
          <Logo />
        </div>
        <Sidebar />
      </div>

      <div className="flex-1 p-8 bg-[#FAF4EB]">
        <div className="flex gap-6">
          {/* Colonna principale sinistra */}
          <div className="flex-1">
            <header className="mb-6">
              <h1 className="text-2xl font-bold">Il Mio Profilo</h1>
              <p className="text-gray-600">Gestisci le tue informazioni personali</p>
            </header>

            {error && (
              <div className="p-4 mb-4 text-red-700 bg-red-100 rounded-lg" role="alert">
                {error}
              </div>
            )}

            {success && (
              <div className="p-4 mb-4 text-green-700 bg-green-100 rounded-lg" role="alert">
                {success}
              </div>
            )}

            <div>
              {/* Cambio Password */}
              <PatientChangePasswordBox />

              {/* Form Principale */}
              <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h2 className="text-lg font-semibold mb-4">Informazioni Personali</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome
                    </label>
                    <input
                        type="text"
                        value={profileData.first_name}
                        onChange={(e) => handleInputChange('first_name', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cognome
                    </label>
                    <input
                        type="text"
                        value={profileData.last_name}
                        onChange={(e) => handleInputChange('last_name', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefono
                    </label>
                    <input
                        type="tel"
                        value={profileData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data di Nascita <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="date"
                        value={profileData.birth_date}
                        onChange={(e) => handleInputChange('birth_date', e.target.value)}
                        className={`w-full p-2 border ${
                            error && error.includes('Luogo di Nascita') && !profileData.birth_place
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:ring-purple-500'
                        } rounded-md focus:ring-2 focus:border-transparent`}
                        required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Luogo di Nascita <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={profileData.birth_place}
                        onChange={(e) => handleInputChange('birth_place', e.target.value)}
                        className={`w-full p-2 border ${
                            error && error.includes('Luogo di Nascita') && !profileData.birth_place
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:ring-purple-500'
                        } rounded-md focus:ring-2 focus:border-transparent`}
                        required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Codice Fiscale (CF) <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={profileData.cf}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (value.length <= 16) {
                            handleInputChange('cf', value);
                          }
                        }}
                        className={`w-full p-2 border ${
                            error && error.includes('Codice Fiscale') && !profileData.cf
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:ring-purple-500'
                        } rounded-md focus:ring-2 focus:border-transparent`}
                        placeholder="Es: RSSMRA80A01H501U"
                        maxLength={16}
                        required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Richiesto per la prenotazione (in alternativa alla P.IVA) - 16 caratteri
                    </p>
                    {profileData.cf && profileData.cf.length > 0 && profileData.cf.length !== 16 && (
                        <p className="text-xs text-red-500 mt-1">
                          Il Codice Fiscale deve essere di 16 caratteri ({profileData.cf.length}/16)
                        </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Partita IVA (P.IVA) <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={profileData.pi}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          if (value.length <= 11) {
                            handleInputChange('pi', value);
                          }
                        }}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Es: 12345678901"
                        maxLength={11}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Richiesta per la prenotazione (in alternativa al CF) - 11 cifre
                    </p>
                    {profileData.pi && profileData.pi.length > 0 && profileData.pi.length !== 11 && (
                        <p className="text-xs text-red-500 mt-1">
                          La Partita IVA deve essere di 11 cifre ({profileData.pi.length}/11)
                        </p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Genere
                    </label>
                    <select
                        value={profileData.gender}
                        onChange={(e) => handleInputChange('gender', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Seleziona genere</option>
                      <option value="M">Maschio</option>
                      <option value="F">Femmina</option>
                      <option value="OTHER">Non Binario</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Note
                    </label>
                    <textarea
                        value={profileData.notes}
                        onChange={(e) => handleInputChange('notes', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Sezione Indirizzo */}
              <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-purple-600"/>
                  Indirizzo
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Via/Strada */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Via/Strada <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={profileData.street}
                        onChange={(e) => handleInputChange('street', e.target.value)}
                        className={`w-full p-2 border ${
                            error && error.includes('Via/Strada') && !profileData.street
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:ring-purple-500'
                        } rounded-md focus:ring-2 focus:border-transparent`}
                        placeholder="Es: Via Roma"
                        required
                    />
                  </div>

                  {/* Numero Civico */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Numero Civico <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={profileData.civic_number}
                      onChange={(e) => handleInputChange('civic_number', e.target.value)}
                      className={`w-full p-2 border ${
                        error && error.includes('civico') && !profileData.civic_number
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:ring-purple-500'
                      } rounded-md focus:ring-2 focus:border-transparent`}
                      placeholder="Es: 123"
                      required
                    />
                  </div>

                  {/* CAP */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CAP <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={profileData.postal_code}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        if (value.length <= 5) {
                          handleInputChange('postal_code', value);
                        }
                      }}
                      className={`w-full p-2 border ${
                        error && error.includes('CAP') && !profileData.postal_code
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:ring-purple-500'
                      } rounded-md focus:ring-2 focus:border-transparent`}
                      placeholder="Es: 00100"
                      maxLength={5}
                      required
                    />
                  </div>

                  {/* Città */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Città <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={profileData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className={`w-full p-2 border ${
                        error && error.includes('Città') && !profileData.city
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:ring-purple-500'
                      } rounded-md focus:ring-2 focus:border-transparent`}
                      placeholder="Es: Roma"
                      required
                    />
                  </div>

                  {/* Provincia */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Provincia <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={profileData.province}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        if (value.length <= 2) {
                          handleInputChange('province', value);
                        }
                      }}
                      className={`w-full p-2 border ${
                        error && error.includes('Provincia') && !profileData.province
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:ring-purple-500'
                      } rounded-md focus:ring-2 focus:border-transparent`}
                      placeholder="Es: RM"
                      maxLength={2}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Sezione Fatturazione */}
              <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-purple-600" />
                  Preferenze Fatturazione
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="invio_fattura_sistema_sanitaria"
                      checked={profileData.invio_fattura_sistema_sanitaria}
                      onChange={(e) => handleCheckboxChange('invio_fattura_sistema_sanitaria', e.target.checked)}
                      className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <div>
                      <label
                        htmlFor="invio_fattura_sistema_sanitaria"
                        className="text-sm font-medium text-gray-700 cursor-pointer"
                      >
                        Invio fattura al Sistema Tessera Sanitaria
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        Abilita l&apos; invio automatico delle fatture sanitarie al Sistema Tessera Sanitaria per la detrazione fiscale
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Note per la fattuazione */}
              <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h2 className="text-lg font-semibold mb-4">Note per la fatturazione</h2>
                <textarea
                  value={profileData.medical_history}
                  onChange={(e) => handleInputChange('medical_history', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows={4}
                />
              </div>

              {/* Info sui campi obbligatori */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <div className="w-5 h-5 text-yellow-600 mt-0.5 mr-3">
                    ℹ️
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-yellow-800 mb-1">
                      Campi richiesti per la prenotazione
                    </h3>
                    <p className="text-sm text-yellow-700">
                      Per poter prenotare appuntamenti è necessario compilare:
                    </p>
                    <ul className="text-sm text-yellow-700 mt-1 ml-4 list-disc">
                      <li><strong>Data e luogo di nascita</strong> - Campi obbligatori</li>
                      <li><strong>Tutti i campi indirizzo</strong> - Via, Civico, CAP, Città, Provincia</li>
                      <li><strong>Almeno uno</strong> tra Codice Fiscale o Partita IVA</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Pulsante Salva */}
              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  className="px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center transition-colors"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Salva Modifiche
                </button>
              </div>
            </div>
          </div>

          {/* Colonna destra con i box informativi */}
          <div className="w-80 space-y-4 mt-[92px]">
            {/* Box Data Registrazione */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Data Registrazione</p>
                  <p className="font-semibold">{formatDate(profileData.created_at)}</p>
                </div>
              </div>
            </div>

            {/* Box Email */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-semibold">{profileData.email}</p>
                </div>
              </div>
            </div>

            {/* Box Telefono */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Phone className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Telefono</p>
                  <p className="font-semibold">{profileData.phone || 'Non specificato'}</p>
                </div>
              </div>
            </div>

            {/* Box Indirizzo */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Indirizzo</p>
                  <p className="font-semibold text-sm">{getFullAddress()}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientProfilePage;
